import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { compareAsc } from "date-fns";
import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";

import { LayoutGrid, List } from "@livechat/design-system-icons";
import {
  Button,
  Icon,
  ModalPortal,
  SearchInput,
  SegmentedControl,
  SpacingToken,
  Tab,
  TabsWrapper,
  Text,
} from "@livechat/design-system-react-components";

import { ViewContainer } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { SectionOnboardingBanner } from "components/section-onboarding-banner/SectionOnboardingBanner";
import { SettingsTable } from "components/settings-table";
import { newFeaturesCampaigns } from "constants/campaigns";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useMacros } from "hooks/use-macros";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { IAutomationViewMode } from "interfaces/automations";
import { AgentsRoutes } from "routes/agents/constants";
import { useLocalStorageSync } from "services/local-storage";
import { selectIsAdmin, selectIsViewer, selectLoggedInAgentPermission } from "store/entities/agents";
import { IMacro } from "store/entities/macros/interfaces";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { AutomationsRoutes } from "../constants";
import { AutomationsEmptyState } from "../shared/components/AutomationsEmptyState";
import { AutomationsSearchEmptyState } from "../shared/components/AutomationsSearchEmptyState";
import { AutomationsSectionHeader } from "../shared/components/AutomationsSectionHeader";
import { AutomationsTemplatesSection } from "../shared/components/AutomationsTemplatesSection";

import { MacroCard } from "./components/MacroCard";
import { MacrosListEmptyState } from "./components/MacrosListEmptyState";
import { PremadeMacroCarousel } from "./components/PremadeMacroCarousel";
import { macroListGridStyles } from "./styles";

type Tab = "all" | "private" | "shared";

interface ITab {
  id: Tab;
  title: string;
}

const TABS: ITab[] = [
  {
    id: "all",
    title: "All",
  },
  {
    id: "private",
    title: "Private",
  },
  {
    id: "shared",
    title: "Shared",
  },
];

const filterMacroByTab =
  (tab: Tab) =>
  (macro: IMacro): boolean => {
    if (tab === "all") {
      return true;
    }

    if (tab === "private") {
      return !!macro.agentId;
    }

    return !macro.agentId;
  };

const filterMacroByQuery =
  (query: string) =>
  (macro: IMacro): boolean => {
    return (
      !query ||
      macro.name.toLowerCase().includes(query.toLowerCase()) ||
      macro.description.toLowerCase().includes(query.toLowerCase())
    );
  };

const filterMacro =
  (query: string, tab: Tab) =>
  (macro: IMacro): boolean => {
    return filterMacroByTab(tab)(macro) && filterMacroByQuery(query)(macro);
  };

export const MacrosList: FC = () => {
  const { macros, deleteMacro, selectMacro } = useMacros();
  const { hash } = useLocation();
  const history = useHistory();
  const isOwner = useSelector(selectIsAdmin);
  const isViewer = useSelector(selectIsViewer);
  const userRole = useSelector(selectLoggedInAgentPermission);
  const [activeTab, setActiveTab] = useState<Tab>("all");
  const [deleteMacroId, setDeleteMacroId] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const handleScroll = (): void => {
    setHasScrolled(true);
  };

  const params = new URLSearchParams(history.location.search);

  const [query, setQuery] = useState(params.get("search") || "");

  const [viewMode, setViewMode] = useLocalStorageSync("macrosViewMode", "grid");

  const handleViewModeChange = (selectedMode: IAutomationViewMode): void => {
    trackEngagement("Macros: View changed", EventPlace.Automations, {
      currentView: selectedMode,
      previousView: viewMode,
    });
    setViewMode(selectedMode);
  };

  const handleQueryChange = (query: string): void => {
    setQuery(query);
    params.set("search", query);
    history.replace(`${AutomationsRoutes.Macros}?${params.toString()}`);
    trackEngagement("Macros: Search performed", EventPlace.Automations, {
      currentView: viewMode,
    });
  };
  const macrosByTab = useMemo(() => macros?.filter(filterMacroByTab(activeTab)), [macros, activeTab]);

  const filteredMacros = useMemo(
    () =>
      macrosByTab
        ?.filter(filterMacroByQuery(query))
        .sort((a: IMacro, b: IMacro) => compareAsc(new Date(a.createdAt), new Date(b.createdAt))),
    [macrosByTab, query]
  );

  const tabHasMacrosNotMatchingQuery = macrosByTab?.some(macro => !filterMacroByQuery(query)(macro));

  const { setAgentFlagCallback: markSectionAsShown } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenMacrosSection.flag,
    newFeaturesCampaigns.hasSeenMacrosSection.campaignStartDate
  );

  useEffect(() => {
    trackEngagement("Automations: Macros section opened", EventPlace.Automations);
    markSectionAsShown();
  }, []);

  const handleCreateMacroClick = (): void => {
    trackEngagement("Automations: Create macro button clicked", EventPlace.Automations, {
      place: macros?.length === 0 ? "empty-state" : "main",
      role: userRole,
    });
    history.push(AutomationsRoutes.AddMacro);
  };

  const handleDeleteMacro = (): void => {
    if (!deleteMacroId) {
      return;
    }
    deleteMacro.mutate({ id: deleteMacroId });
    trackEngagement("Macros - listing: Macro deleted", EventPlace.Automations);
    setDeleteMacroId(null);
  };

  const handleCloseModal = (): void => {
    setDeleteMacroId(null);
  };

  return (
    <ViewContainer title="Macros" contentCss={css({ overflowY: "scroll" })}>
      <Flex padding={withVar(SpacingToken.Spacing8)} justifyContent="center">
        <Flex maxWidth="960px" width="100%" column gap={withVar(SpacingToken.Spacing8)}>
          <SectionOnboardingBanner section="macros" autoDisplayForAllUsers />
          {macros?.length === 0 ? (
            <AutomationsEmptyState
              title="No macros yet"
              description="Create your first macro and make your work easier. You can update tickets with a series of pre-set actions with just one click."
              primaryAction={{
                label: "Create macro",
                onClick: handleCreateMacroClick,
                disabled: isViewer,
                testId: "empty-state-cta-button",
              }}
              templates={<PremadeMacroCarousel />}
              contentWidth={600}
            />
          ) : (
            <>
              <Block>
                <Button kind="primary" onClick={handleCreateMacroClick} disabled={isViewer}>
                  Create macro
                </Button>
              </Block>
              <AutomationsTemplatesSection
                description="Select from agents' favorite macros templates and adjust them to your case."
                templates={<PremadeMacroCarousel />}
              />
              <Flex column gap={withVar(SpacingToken.Spacing4)}>
                <AutomationsSectionHeader title="Created macros" testId="created-macros-section" />
                <Flex justifyContent="space-between">
                  <SegmentedControl
                    buttons={[
                      { id: "grid", icon: <Icon source={LayoutGrid} /> },
                      { id: "list", icon: <Icon source={List} /> },
                    ]}
                    currentId={viewMode}
                    size="compact"
                    onButtonClick={id => handleViewModeChange(id as IAutomationViewMode)}
                  />
                  <AutoWidthSearchInput
                    size="compact"
                    value={query}
                    onChange={handleQueryChange}
                    placeholder="Search…"
                  />
                </Flex>
                <FlexTabsWrapper>
                  {TABS.map(({ id, title }) => (
                    <Tab
                      key={id}
                      id={id}
                      isSelected={activeTab === id}
                      onClick={() => setActiveTab(id)}
                      count={macros?.filter(filterMacro(query, id)).length}
                    >
                      {title}
                    </Tab>
                  ))}
                </FlexTabsWrapper>
                {macrosByTab?.length === 0 && (
                  <>
                    {activeTab === "private" && (
                      <MacrosListEmptyState
                        title="No private macros yet"
                        description="Create a private macro and make your work easier. You can update tickets with a series of pre-set actions with just one click."
                        primaryAction={{
                          label: "Create macro",
                          disabled: isViewer,
                          onClick: () => history.push(AutomationsRoutes.AddMacro),
                        }}
                      />
                    )}
                    {activeTab === "shared" && (
                      <MacrosListEmptyState
                        title="No shared macros yet"
                        description={
                          isOwner
                            ? "Create a shared macro to streamline team efforts. You can update tickets with a series of pre-set actions with just one click."
                            : "You can't create shared macros in your current HelpDesk role. Contact any admin to create one for your team."
                        }
                        primaryAction={{
                          label: isOwner ? "Create macro" : "Contact any admin",
                          disabled: isViewer,
                          onClick: () => history.push(isOwner ? AutomationsRoutes.AddMacro : AgentsRoutes.Agents),
                        }}
                      />
                    )}
                  </>
                )}
                {tabHasMacrosNotMatchingQuery && filteredMacros?.length === 0 && (
                  <AutomationsSearchEmptyState
                    title="No macros found"
                    description="Adjust search criteria or show all macros"
                    primaryAction={{ label: "Show all macros", onClick: () => handleQueryChange("") }}
                  />
                )}
                {viewMode === "list" && filteredMacros && filteredMacros.length > 0 ? (
                  <SettingsTable
                    marginTop={withVar(SpacingToken.Spacing4)}
                    marginBottom="0"
                    columns={5}
                    columnSizes="2fr 2fr 1fr 1fr min-content"
                    headers={["Name", "Description", "Teams", "Type", ""]}
                    data-testid="macros-table"
                  >
                    {filteredMacros?.map((macro: IMacro) => (
                      <MacroCard
                        key={macro.id}
                        macro={macro}
                        setDeleteMacroId={setDeleteMacroId}
                        highlight={hash === `#${macro.id}`}
                        displayMode="list"
                        hasScrolled={hasScrolled}
                        onScroll={handleScroll}
                      />
                    ))}
                  </SettingsTable>
                ) : (
                  <Block css={macroListGridStyles} data-testid="macros-grid">
                    {filteredMacros?.map((macro: IMacro) => (
                      <MacroCard
                        key={macro.id}
                        macro={macro}
                        setDeleteMacroId={setDeleteMacroId}
                        highlight={hash === `#${macro.id}`}
                        displayMode="grid"
                        hasScrolled={hasScrolled}
                        onScroll={handleScroll}
                      />
                    ))}
                  </Block>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      {deleteMacroId && (
        <ModalPortal zIndex={StackingContextLevel.Modal}>
          <ConfirmActionModal
            heading="Delete macro?"
            onClose={handleCloseModal}
            primaryAction={{
              label: "Delete",
              actionType: "destructive",
              onClick: handleDeleteMacro,
            }}
            secondaryAction={{
              label: "Cancel",
              onClick: handleCloseModal,
            }}
          >
            <Text>
              You&apos;re about to remove the <b>{selectMacro({ id: deleteMacroId })?.name}</b> macro. Are you sure you
              want to continue?
            </Text>
          </ConfirmActionModal>
        </ModalPortal>
      )}
    </ViewContainer>
  );
};

const AutoWidthSearchInput = styled(SearchInput)`
  width: auto;
`;

const FlexTabsWrapper = styled(TabsWrapper)`
  display: flex;
`;
